import React from 'react';
import { motion } from 'framer-motion';
import { 
  Factory,
  Zap,
  Droplets,
  Gauge,
  Magnet,
  FileText,
  ArrowRight,
  TrendingUp,
  Users,
  Award
} from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: Factory,
      title: 'Manufacturing',
      description: 'Optimize production efficiency and equipment reliability across diverse manufacturing environments.',
      challenges: [
        'Unplanned equipment downtime',
        'Quality control issues',
        'Energy inefficiencies',
        'Supply chain disruptions'
      ],
      solutions: [
        'Real-time equipment monitoring',
        'Predictive maintenance scheduling',
        'Quality trend analysis',
        'Energy consumption optimization'
      ],
      caseExample: {
        client: 'Automotive Parts Manufacturer',
        result: '42% reduction in unplanned downtime, $1.8M annual savings'
      },
      image: '/images/industry/Manufacturing.png'
    },
    {
      icon: Zap,
      title: 'Energy & Power',
      description: 'Ensure reliable power generation and distribution with advanced monitoring and predictive maintenance.',
      challenges: [
        'Critical equipment failures',
        'Regulatory compliance',
        'Grid stability issues',
        'Environmental monitoring'
      ],
      solutions: [
        'Turbine health monitoring',
        'Transformer condition assessment',
        'Grid stability analytics',
        'Emissions tracking'
      ],
      caseExample: {
        client: 'Regional Power Company',
        result: '73% reduction in forced outages, $2.4M annual cost savings'
      },
      image: '/images/industry/Energy and Power.png'
    },
    {
      icon: Droplets,
      title: 'Oil & Gas',
      description: 'Maximize uptime and safety in upstream, midstream, and downstream operations.',
      challenges: [
        'Safety and environmental risks',
        'Remote location monitoring',
        'Equipment corrosion',
        'Regulatory compliance'
      ],
      solutions: [
        'Pipeline integrity monitoring',
        'Pump and compressor analytics',
        'Corrosion monitoring systems',
        'Safety system optimization'
      ],
      caseExample: {
        client: 'Offshore Oil Platform',
        result: '35% improvement in equipment reliability, zero safety incidents'
      },
      image: '/images/industry/Oil and Gas.png'
    },
    {
      icon: Gauge,
      title: 'Mining',
      description: 'Optimize extraction operations and ensure equipment reliability in harsh environments.',
      challenges: [
        'Extreme operating conditions',
        'High equipment costs',
        'Safety requirements',
        'Environmental impact'
      ],
      solutions: [
        'Conveyor system monitoring',
        'Crusher performance optimization',
        'Truck fleet analytics',
        'Environmental monitoring'
      ],
      caseExample: {
        client: 'Copper Mining Operation',
        result: '28% increase in conveyor availability, $3.2M savings annually'
      },
      image: '/images/industry/Mining.png'
    },
    {
      icon: Magnet,
      title: 'Chemical Processing',
      description: 'Maintain process integrity and equipment reliability in complex chemical environments.',
      challenges: [
        'Process variability',
        'Corrosive environments',
        'Safety critical operations',
        'Product quality consistency'
      ],
      solutions: [
        'Reactor monitoring systems',
        'Heat exchanger optimization',
        'Pump seal monitoring',
        'Process deviation alerts'
      ],
      caseExample: {
        client: 'Petrochemical Facility',
        result: '51% reduction in process upsets, improved product quality'
      },
      image: '/images/industry/Chemical.png'
    },
    {
      icon: FileText,
      title: 'Pulp & Paper',
      description: 'Optimize paper production processes and reduce maintenance costs through intelligent monitoring.',
      challenges: [
        'Continuous production requirements',
        'Paper quality variations',
        'High energy consumption',
        'Maintenance scheduling'
      ],
      solutions: [
        'Paper machine monitoring',
        'Pulp digester optimization',
        'Steam system efficiency',
        'Quality control analytics'
      ],
      caseExample: {
        client: 'Paper Manufacturing Plant',
        result: '38% improvement in paper quality, 22% energy reduction'
      },
      image: '/images/industry/Pulp and Paper.png'
    }
  ];

  const stats = [
    { icon: Users, value: '20X', label: 'ROI Achievement' },
    { icon: Factory, value: '99.98%', label: 'Machine Availability' },
    { icon: Award, value: '98%', label: 'Downtime Reduction' },
    { icon: TrendingUp, value: '24×7', label: 'Continued Support' }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F0F4FE] via-white to-[#E2EEFE] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              Industry Expertise Across <span className="text-[#233790]">Critical Sectors</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Delivering specialized asset management solutions tailored to the unique challenges 
              and requirements of diverse industrial environments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-light text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className={`bg-white rounded-2xl p-8 shadow-lg ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mr-4">
                        <industry.icon className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-3xl font-light text-gray-900">{industry.title}</h2>
                    </div>
                    
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                      {industry.description}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Challenges</h3>
                        <ul className="space-y-2">
                          {industry.challenges.map((challenge, idx) => (
                            <li key={idx} className="text-gray-600 flex items-start">
                              <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              {challenge}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Solutions</h3>
                        <ul className="space-y-2">
                          {industry.solutions.map((solution, idx) => (
                            <li key={idx} className="text-gray-600 flex items-start">
                              <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Success Story</h4>
                      <p className="text-gray-700 mb-2">{industry.caseExample.client}</p>
                      <p className="text-[#233790] font-medium">{industry.caseExample.result}</p>
                    </div>
                  </div>

                  {/* Image */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="relative aspect-square w-full">
                      <img
                        src={industry.image}
                        alt={industry.title}
                        className="w-full h-full object-contain rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#F0F4FE] to-[#E2EEFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Ready to Transform Your Industry Operations?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Let our industry experts assess your specific challenges and develop 
              a customized asset management strategy for your sector.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors"
              >
                Schedule Industry Consultation
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
              >
                View Industry Case Studies <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;